import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Bold, Code, Italic, Underline, Strikethrough, Superscript, Subscript } from '@ckeditor/ckeditor5-basic-styles';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Clipboard } from "@ckeditor/ckeditor5-clipboard";
import TwoColumnsBox from './plugins/two-columns/src/two-columns';
import { List, TodoList } from '@ckeditor/ckeditor5-list';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { FontSize, FontColor } from '@ckeditor/ckeditor5-font';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { Indent } from '@ckeditor/ckeditor5-indent';
import { Link, LinkImage } from "./plugins/link";
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { Table, TableCellProperties, TableProperties, TableToolbar } from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import IframeEmbed from './plugins/iframe/src/iframeembed';
import Shortcodes from "./plugins/shortcodes/src/shortcodes";
import ContentTemplates from './plugins/content-templates/content-templates.js';
import ThreeColumnsBox from './plugins/three-columns/src/three-columns.js';
import FourColumnsBox from './plugins/four-columns/src/four-columns.js';
import { SimpleUploadAdapter } from '@ckeditor/ckeditor5-upload';
import { Mention } from '@ckeditor/ckeditor5-mention';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';

window.editorSettings = window.editorSettings || {
    h2Support: false,
    fontColors: [],
    shortcodes: [],
    contentTemplates: [],
    uploadEndpoint: '/media/images/upload',
};

// @-mentions (fields with `editor--mentions`): the feed comes from `data-mentions-url`, fetched once per url
const mentionCache = new Map();

function mentionFeed(field, query) {
    const url = field.dataset.mentionsUrl;
    if (! url) {
        return Promise.resolve([]);
    }
    if (! mentionCache.has(url)) {
        mentionCache.set(url, fetch(url, { credentials: 'same-origin' }).then((r) => r.ok ? r.json() : []).catch(() => []));
    }
    // diacritics-insensitive substring match ("rezac" finds "Řezáč")
    const fold = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const needle = fold(query);
    return mentionCache.get(url).then((items) => items.filter((item) => fold(item.name).includes(needle)).slice(0, 10));
}

function mentionItemRenderer(item) {
    const el = document.createElement('span');
    el.classList.add('custom-mention-item');
    const img = document.createElement('img');
    img.src = item.avatarUrl;
    img.alt = '';
    img.width = 24;
    img.height = 24;
    img.style.cssText = 'border-radius:9999px;margin-right:8px;vertical-align:middle;';
    el.appendChild(img);
    el.appendChild(document.createTextNode(item.name));
    return el;
}

// store `<span class="mention" data-user-id="…">@Name</span>` — the id is what the server trusts
function MentionCustomization(editor) {
    editor.conversion.for('upcast').elementToAttribute({
        view: { name: 'span', key: 'data-mention', classes: 'mention', attributes: { 'data-user-id': true } },
        model: {
            key: 'mention',
            value: (viewItem) => editor.plugins.get('Mention').toMentionAttribute(viewItem, {
                userId: viewItem.getAttribute('data-user-id'),
            }),
        },
        converterPriority: 'high',
    });

    editor.conversion.for('downcast').attributeToElement({
        model: 'mention',
        view: (modelAttributeValue, { writer }) => {
            if (! modelAttributeValue) {
                return;
            }
            return writer.createAttributeElement('span', {
                class: 'mention',
                'data-mention': modelAttributeValue.id,
                'data-user-id': modelAttributeValue.userId,
            }, { priority: 20, id: modelAttributeValue.uid });
        },
        converterPriority: 'high',
    });
}

function initEditor(field, callback) {
    if (typeof field._editor !== 'undefined') {
        return;
    }

    ClassicEditor
        .create(field, getConfiguration(field))
        .then(editor => {
            field._editor = editor;
            const editingView = editor.editing.view;

            // pasting clipboard to plaintext
            editingView.document.on('clipboardInput', (event, data) => {
                let content = data.dataTransfer.getData('text/html');



                if (editor.isReadOnly || data.method !== 'paste') {
                    return;
                }

                // No HTML on the clipboard (plain text from Sublime/terminal, or an image) — bail so CKEditor's
                // default pipeline pastes the text/plain (or uploads the image). Overwriting data.content with an
                // empty processed value here silently dropped every non-HTML paste.
                if (! content) {
                    return;
                }

                content = content.replace('<meta charset="UTF-8">', '');
                let node = document.createElement('div');
                node.innerHTML = content;

                let nodeArr = node.querySelectorAll('*');
                let allowed = {
                        'IMG': [
                            'src',
                            'width',
                            'height',
                        ],
                        'IFRAME': [
                            'src',
                            'height',
                        ],
                        'A': [
                            'href',
                            'target',
                        ],
                        // pasted code blocks keep their language-* marker
                        'CODE': [
                            'class',
                        ],
                    };


                nodeArr.forEach(node => {
                    [...node.attributes].forEach(attr => {
                        if(allowed[node.tagName] && allowed[node.tagName].includes(attr.name)) {
                            return;
                        }

                        node.removeAttribute(attr.name);
                    });
                });

                data.content = editor.data.htmlProcessor.toView(node.innerHTML);
                editingView.scrollToTheSelection();

            }, {
                priority: 'high'
            });

            if (callback) {
                callback(editor);
            }

        })
        .catch(error => {
            console.error(error);
        });

    function getConfiguration(field) {
        let imagesSupport = field.classList.contains('editor--images');
        let todoSupport = field.classList.contains('editor--todo');
        // e-mail body editor: a deliberately narrow toolbar (no tables/columns/iframe/images/colors)
        // so client mails stay inside what the mail shell (mail.latte) knows how to render
        let emailMode = field.classList.contains('editor--email');
        let mentionsSupport = field.classList.contains('editor--mentions');
        let configuration = {
            language: document.documentElement.lang,
            plugins: getConfigurationPlugins(imagesSupport, todoSupport, emailMode, mentionsSupport),
            mention: mentionsSupport ? {
                feeds: [{
                    marker: '@',
                    minimumCharacters: 0,
                    feed: (query) => mentionFeed(field, query),
                    itemRenderer: mentionItemRenderer,
                }],
            } : undefined,
            toolbar: {
                items: getConfigurationToolbarItems(imagesSupport, todoSupport, emailMode),
                shouldNotGroupWhenFull: true,

            },
            // dev shop: multi-line snippets as <pre><code class="language-…"> (styled by .typography pre/code)
            codeBlock: {
                languages: [
                    { language: 'plaintext', label: 'Text' },
                    { language: 'bash', label: 'Bash' },
                    { language: 'css', label: 'CSS' },
                    { language: 'diff', label: 'Diff' },
                    { language: 'html', label: 'HTML' },
                    { language: 'javascript', label: 'JavaScript' },
                    { language: 'json', label: 'JSON' },
                    { language: 'php', label: 'PHP' },
                    { language: 'python', label: 'Python' },
                    { language: 'sql', label: 'SQL' },
                    { language: 'typescript', label: 'TypeScript' },
                    { language: 'xml', label: 'XML' },
                    { language: 'yaml', label: 'YAML' },
                ],
            },


            table: {
                contentToolbar: [
                    'tableColumn',
                    'tableRow',
                    'mergeTableCells',
                    'tableCellProperties',
                    'tableProperties'
                ],
                tableProperties: {
                    borderColors: getConfigurationTableColors(),
                    backgroundColors: getConfigurationTableColors()
                },

                // Set the palettes for table cells.
                tableCellProperties: {
                    borderColors: getConfigurationTableColors(),
                    backgroundColors: getConfigurationTableColors()
                }
            },
            mediaEmbed: {
            //     removeProviders: ['instagram', 'twitter', 'googleMaps', 'flickr', 'facebook'],
                previewsInData: true
            },
            link: {
                decorators: {
                    openInNewTab: {
                        mode: 'manual',
                        label: 'Open in a new tab',
                        attributes: {
                            target: '_blank',
                            rel: 'noopener'
                        }
                    },
                    openInNewTabNofollow: {
                        mode: 'manual',
                        label: translate('Open in a new tab + NOFOLLOW'),
                        attributes: {
                            target: '_blank',
                            rel: 'noopener nofollow'
                        }
                    },
                    nofollow: {
                        mode: 'manual',
                        label: translate('NOFOLLOW'),
                        attributes: {
                            rel: 'nofollow'
                        }
                    }
                }
            },
            heading: getConfigurationHeadings(field, emailMode),
            fontColor: getConfigurationFontColors(),
        };

        if (imagesSupport) {
            configuration['image'] = {
                resizeUnit: "%",
                resizeOptions: [{
                    name: 'resizeImage:original',
                    value: null,
                    icon: 'original'
                }, {
                    name: 'resizeImage:10',
                    value: '10',
                    icon: 'small'
                }, {
                    name: 'resizeImage:25',
                    value: '25',
                    icon: 'small'
                }, {
                    name: 'resizeImage:50',
                    value: '50',
                    icon: 'medium'
                }, {
                    name: 'resizeImage:75',
                    value: '75',
                    icon: 'large'
                },
                {
                    name: 'resizeImage:100',
                    value: '100',
                    icon: 'large'
                }],
                toolbar: [
                    'imageTextAlternative',
                    'toggleImageCaption',
                    'linkImage',
                    '|',
                    'imageStyle:inline',
                    'imageStyle:alignLeft',
                    'imageStyle:alignCenter',
                    'imageStyle:alignRight',
                    '|',
                    'resizeImage',,
                ]
            };

            configuration['simpleUpload'] = {
                uploadUrl: window.editorSettings.uploadEndpoint,
            };
        }

        return configuration;
    }

    function getConfigurationPlugins(imagesSupport, todoSupport, emailMode, mentionsSupport = false) {
        if (emailMode) {
            return [
                ClassicEditor,
                Essentials,
                Paragraph,
                Heading,
                Bold,
                Italic,
                Underline,
                Link,
                List,
                RemoveFormat,
            ];
        }

        let plugins = [
            ClassicEditor,
            Alignment,
            BlockQuote,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Code,
            CodeBlock,
            Superscript,
            Essentials,
            Subscript,
            Clipboard,
            TwoColumnsBox,
            FontColor,
            FontSize,
            Heading,
            HorizontalLine,
            Indent,
            Link,
            List,
            MediaEmbed,
            Paragraph,
            RemoveFormat,
            Table,
            TableCellProperties,
            TableProperties,
            TableToolbar,
            TextTransformation,
            IframeEmbed,
            ThreeColumnsBox,
            FourColumnsBox,
            SimpleUploadAdapter,
        ];

        if (todoSupport) {
            plugins.push(TodoList);
        }

        if (mentionsSupport) {
            plugins.push(Mention, MentionCustomization);
        }

        if (window.editorSettings.shortcodes.length) {
            plugins.push(Shortcodes);
        }

        if (window.editorSettings.contentTemplates.length) {
            plugins.push(ContentTemplates);
        }

        if (imagesSupport) {
            plugins.push(
                Image,
                ImageCaption,
                ImageResize,
                ImageStyle,
                ImageToolbar,
                ImageUpload,
                LinkImage,
                SimpleUploadAdapter,
            );
        }

        return plugins;
    }

    function getConfigurationToolbarItems(imagesSupport, todoSupport, emailMode) {
        if (emailMode) {
            return [
                'heading',
                '|',
                'bold',
                'italic',
                'underline',
                '|',
                'bulletedList',
                'numberedList',
                '|',
                'link',
                '|',
                'removeFormat',
                'undo',
                'redo',
            ];
        }

        let toolbarItems = [
            'heading',
            '|',
            'alignment',
            'fontColor',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'code',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'link',
            'insertTable',
            'mediaEmbed',
            'iframeEmbed',
        ];

        if (todoSupport) {
            const at = toolbarItems.indexOf('numberedList');
            toolbarItems.splice(at + 1, 0, 'todoList');
        }

        if (imagesSupport) {
            toolbarItems.push('imageUpload');
        }

        toolbarItems.push('blockQuote', 'codeBlock');

        if (window.editorSettings.shortcodes.length) {
            toolbarItems.push('shortcodes');
        }

        if (window.editorSettings.contentTemplates.length) {
            toolbarItems.push('contentTemplates');
        }

        toolbarItems.push(
            'horizontalLine',
            '|',
            'twoColumnsBox',
            'threeColumnsBox',
            'fourColumnsBox',
            '|',
            'superscript',
            'subscript',
            '|',
            'removeFormat',
            'undo',
            'redo',
        );

        return toolbarItems;
    }

    function getConfigurationHeadings(field, emailMode) {
        if (emailMode) {
            // paragraph + H2/H3 only — the mail shell styles these; H1/H4 it does not
            return {
                options: [
                    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                ]
            };
        }

        let counter = 0;
        let headings = {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            ]
        };

        if (field.dataset.h1Support) {
            headings.options.push({ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading' + (++counter) });
        }

        if (field.dataset.h2Support || window.editorSettings.h2Support) {
            headings.options.push({ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading' + (++counter) });
        }

        headings.options.push({ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading' + (++counter) });
        headings.options.push({ model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading' + (++counter) });

        return headings;
    }

    function getConfigurationTableColors() {
        let config = [];
        config =  window.editorSettings.tableColors;
        return config;
    }

    function getConfigurationFontColors() {
        let config = {
            colors: [
                // Black / Gray
                {
                    color: '#000000',
                },
                {
                    color: '#111727',
                },
                {
                    color: '#202938',
                },
                {
                    color: '#384152',
                },
                {
                    color: '#4c5564',
                },
                {
                    color: '#6a7380',
                },
                {
                    color: '#9da2ae',
                },
                {
                    color: '#d2d3de',
                    hasBorder: true,
                },
                {
                    color: '#e6e8ec',
                    hasBorder: true,
                },
                {
                    color: '#f3f5f9',
                    hasBorder: true,
                },

                // Red
                {
                    color: '#7f1d1e',
                },
                {
                    color: '#9b1b1d',
                },
                {
                    color: '#b91b20',
                },
                {
                    color: '#dc2624',
                },
                {
                    color: '#f04342',
                },
                {
                    color: '#f77070',
                },
                {
                    color: '#fca5a7',
                },
                {
                    color: '#fdcbcc',
                    hasBorder: true,
                },
                {
                    color: '#ffe1e0',
                    hasBorder: true,
                },
                {
                    color: '#fcf3f2',
                    hasBorder: true,
                },

                // Yellow
                {
                    color: '#793410',
                },
                {
                    color: '#933f0e',
                },
                {
                    color: '#933f0e',
                },
                {
                    color: '#d87706',
                },
                {
                    color: '#f39e0b',
                },
                {
                    color: '#fcbf22',
                },
                {
                    color: '#f9d44c',
                },
                {
                    color: '#fbe787',
                    hasBorder: true,
                },
                {
                    color: '#fff2c7',
                    hasBorder: true,
                },
                {
                    color: '#fffbea',
                    hasBorder: true,
                },

                // Green
                {
                    color: '#074c3c',
                },
                {
                    color: '#076046',
                },
                {
                    color: '#057758',
                },
                {
                    color: '#07966d',
                },
                {
                    color: '#10b884',
                },
                {
                    color: '#31d59b',
                },
                {
                    color: '#6de7b5',
                },
                {
                    color: '#a7f4ce',
                    hasBorder: true,
                },
                {
                    color: '#d0fbe3',
                    hasBorder: true,
                },
                {
                    color: '#ecfdf7',
                    hasBorder: true,
                },

                // Blue
                {
                    color: '#1b3f87',
                },
                {
                    color: '#1e40ad',
                },
                {
                    color: '#1c4fd8',
                },
                {
                    color: '#2463e9',
                },
                {
                    color: '#3c82f5',
                },
                {
                    color: '#61a6fa',
                },
                {
                    color: '#93c5fd',
                },
                {
                    color: '#bfdcfe',
                    hasBorder: true,
                },
                {
                    color: '#dbecff',
                    hasBorder: true,
                },
                {
                    color: '#f0f5ff',
                    hasBorder: true,
                },

                // Indigo
                {
                    color: '#322d81',
                },
                {
                    color: '#3530a4',
                },
                {
                    color: '#4539ca',
                },
                {
                    color: '#5046e4',
                },
                {
                    color: '#6365f2',
                },
                {
                    color: '#7d8ff4',
                },
                {
                    color: '#a6b4fd',
                },
                {
                    color: '#c7d2fe',
                    hasBorder: true,
                },
                {
                    color: '#e3e6fe',
                    hasBorder: true,
                },
                {
                    color: '#eff1fe',
                    hasBorder: true,
                },

                // Purple
                {
                    color: '#4f1b9a',
                },
                {
                    color: '#5c1fb5',
                },
                {
                    color: '#6d27da',
                },
                {
                    color: '#7b3be9',
                },
                {
                    color: '#8b5cf4',
                },
                {
                    color: '#a78cfb',
                },
                {
                    color: '#c6b6fb',
                },
                {
                    color: '#ddd7ff',
                    hasBorder: true,
                },
                {
                    color: '#eeeaff',
                    hasBorder: true,
                },
                {
                    color: '#f6f4ff',
                    hasBorder: true,
                },

                // Pink
                {
                    color: '#811843',
                },
                {
                    color: '#9c174b',
                },
                {
                    color: '#bc195e',
                },
                {
                    color: '#db2677',
                },
                {
                    color: '#ec489b',
                },
                {
                    color: '#f574b6',
                },
                {
                    color: '#f8a8d3',
                },
                {
                    color: '#fccfe8',
                    hasBorder: true,
                },
                {
                    color: '#fde7f3',
                    hasBorder: true,
                },
                {
                    color: '#fcf2f8',
                    hasBorder: true,
                },
            ],
            columns: 10,
        };

        window.editorSettings.fontColors.forEach(function (definition) {
            config.colors.push({
                color: definition,
            });
        });

        return config;
    }

}

window['initEditor'] = initEditor;

// translations
window.addEventListener('load', () => {
    defineTranslations('sk', {
        'Embed iframe': 'Vloženie prvku iframe',
        'In line': 'V súlade',
        'Insert paragraph after block': 'Vloženie odseku podľa bloku',
        'Insert paragraph before block': 'Vloženie odseku pred blok',
        'Link image': 'Odkaz na obrázok',
        'Open in a new tab + NOFOLLOW': 'Otvoriť v novej karte + NOFOLLOW',
        'Original': 'Originál',
        'Resize image': 'Zmena veľkosti obrázka',
        'Toggle caption off': 'Vypnutie titulku',
        'Toggle caption on': 'Zapnutie titulku',
        'URL address': 'Adresa URL',
        'Insert template': 'Vložit šablonu',
        'Insert code block': 'Vložiť blok kódu',
        'Code block': 'Blok kódu',
        'Code': 'Kód',
        'Plain text': 'Obyčajný text',
    });

    defineTranslations('cs', {
        'Embed iframe': 'Vložit prvek iframe',
        'In line': 'V řadě',
        'Insert paragraph after block': 'Vložit odstavec po bloku',
        'Insert paragraph before block': 'Vložit odstavec před blok',
        'Link image': 'Odkaz obrázku',
        'Open in a new tab + NOFOLLOW': 'Otevřít v nové kartě + NOFOLLOW',
        'Original': 'Originál',
        'Resize image': 'Změnit velikost obrázku',
        'Toggle caption off': 'Vypnout popisek',
        'Toggle caption on': 'Zapnout popisek',
        'URL address': 'URL adresa',
        'Insert template': 'Vložit šablonu',
        'Title': 'Tutilek',
        'Insert code block': 'Vložit blok kódu',
        'Code block': 'Blok kódu',
        'Code': 'Kód',
        'Plain text': 'Prostý text',
    });
});

function defineTranslations(locale, translations) {
    window.CKEDITOR_TRANSLATIONS = window.CKEDITOR_TRANSLATIONS || {};
    window.CKEDITOR_TRANSLATIONS[locale] = window.CKEDITOR_TRANSLATIONS[locale] || {};
    window.CKEDITOR_TRANSLATIONS[locale].dictionary = window.CKEDITOR_TRANSLATIONS[locale].dictionary || {};

    Object.assign(window.CKEDITOR_TRANSLATIONS[locale].dictionary, translations);
}

function translate(message) {
    let locale = document.documentElement.lang;
    defineTranslations(locale, {});

    return window.CKEDITOR_TRANSLATIONS[locale].dictionary[message] || message;
}
